import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Spinner } from '@telegram-apps/telegram-ui';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  MEDIA_TYPE_LABELS,
  STATUS_LABELS,
  type Status,
} from '@mass-cult/shared';
import { EmptyState, ErrorState, Poster } from '../../components/ui';
import {
  addToFolder,
  getFolders,
  getTitle,
  removeFromFolder,
  resolveImageUrl,
  setRating,
  setStatus,
} from '../../shared/api/client';

export function TitlePage() {
  const { mediaType = 'movie', tmdbId = '0' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const numericId = Number(tmdbId);

  const titleQuery = useQuery({
    queryKey: ['title', mediaType, numericId],
    queryFn: () => getTitle(mediaType, numericId),
    enabled: !Number.isNaN(numericId),
  });

  const foldersQuery = useQuery({
    queryKey: ['folders'],
    queryFn: getFolders,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['title', mediaType, numericId] });
    void queryClient.invalidateQueries({ queryKey: ['library'] });
    void queryClient.invalidateQueries({ queryKey: ['folders'] });
  };

  const statusMutation = useMutation({
    mutationFn: (status: Status | null) => setStatus(mediaType, numericId, status),
    onSuccess: invalidate,
  });

  const ratingMutation = useMutation({
    mutationFn: (rating: number | null) => setRating(mediaType, numericId, rating),
    onSuccess: invalidate,
  });

  const folderMutation = useMutation({
    mutationFn: ({ folderId, add }: { folderId: number; add: boolean }) =>
      add
        ? addToFolder(folderId, mediaType, numericId)
        : removeFromFolder(folderId, mediaType, numericId),
    onSuccess: invalidate,
  });

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (!tg) return;
    tg.BackButton.show();
    const onBack = () => navigate(-1);
    tg.BackButton.onClick(onBack);
    return () => {
      tg.BackButton.offClick(onBack);
      tg.BackButton.hide();
    };
  }, [navigate]);

  if (titleQuery.isLoading) return <Spinner size="m" />;
  if (titleQuery.isError) {
    return <ErrorState message={(titleQuery.error as Error).message} />;
  }
  if (!titleQuery.data) return <EmptyState title="Карточка не найдена" />;

  const title = titleQuery.data;

  return (
    <div>
      {title.backdropUrl && (
        <img src={resolveImageUrl(title.backdropUrl) ?? undefined} alt="" className="backdrop" />
      )}
      <div style={{ display: 'flex', gap: 12 }}>
        <Poster url={title.posterUrl} alt={title.title} className="poster-large" />
        <div>
          <h1 style={{ margin: 0 }}>{title.title}</h1>
          {title.titleOriginal && title.titleOriginal !== title.title && (
            <div className="meta">{title.titleOriginal}</div>
          )}
          <div className="meta">
            {[title.year, MEDIA_TYPE_LABELS[title.mediaType], title.tmdbRating?.toFixed(1)]
              .filter(Boolean)
              .join(' · ')}
          </div>
          {title.stale && <div className="meta">Показаны кэшированные данные TMDB</div>}
        </div>
      </div>

      <div className="section-title">Статус</div>
      <div className="chips">
        {(Object.keys(STATUS_LABELS) as Status[]).map((status) => (
          <button
            key={status}
            className={`chip ${title.status === status ? 'active' : ''}`}
            onClick={() =>
              statusMutation.mutate(title.status === status ? null : status)
            }
          >
            {STATUS_LABELS[status]}
          </button>
        ))}
      </div>

      <div className="section-title">Оценка</div>
      <div className="rating-row">
        {Array.from({ length: 10 }, (_, index) => index + 1).map((value) => (
          <button
            key={value}
            className={`rating-btn ${title.rating === value ? 'active' : ''}`}
            onClick={() =>
              ratingMutation.mutate(title.rating === value ? null : value)
            }
          >
            {value}
          </button>
        ))}
      </div>

      <div className="section-title">Папки</div>
      {foldersQuery.data?.map((folder) => {
        const selected = title.folderIds.includes(folder.id);
        return (
          <Button
            key={folder.id}
            mode={selected ? 'filled' : 'bezeled'}
            stretched
            style={{ marginBottom: 8 }}
            onClick={() =>
              folderMutation.mutate({ folderId: folder.id, add: !selected })
            }
          >
            {selected ? '✓ ' : ''}
            {folder.name}
          </Button>
        );
      })}

      <div className="section-title">Описание</div>
      <p>{title.overview ?? 'Описание отсутствует'}</p>

      {title.genres.length > 0 && (
        <>
          <div className="section-title">Жанры</div>
          <div className="meta">{title.genres.join(', ')}</div>
        </>
      )}

      {title.cast.length > 0 && (
        <>
          <div className="section-title">Актёрский состав</div>
          {title.cast.map((member) => (
            <div key={member.id} style={{ marginBottom: 8 }}>
              <strong>{member.name}</strong>
              {member.character ? ` — ${member.character}` : ''}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
